import React from 'react';
import { GlassCard } from './ui/GlassCard';
import { Input, Toggle, SegmentedControl, FormPill } from './ui/Controls';
import { Activity, ShieldAlert, TrendingUp, Swords, Home, Target, Flame, UserX } from 'lucide-react';
export interface MatchState {
  teamA: string;
  teamB: string;
  oddsA: string;
  oddsB: string;
  isHomeA: boolean;
  formA: ('G' | 'E' | 'P' | null)[];
  formB: ('G' | 'E' | 'P' | null)[];
  injuryA: string;
  injuryB: string;
  homeWinRateA: string;
  awayWinRateA: string;
  homeWinRateB: string;
  awayWinRateB: string;
  avgGoalsScoredA: string;
  avgGoalsConcededA: string;
  avgGoalsScoredB: string;
  avgGoalsConcededB: string;
  motivationA: string;
  motivationB: string;
  suspensionsA: string;
  suspensionsB: string;
}
interface LeftPanelProps {
  state: MatchState;
  setState: React.Dispatch<React.SetStateAction<MatchState>>;
  onAnalyze: () => void;
  isAnalyzing: boolean;
}
export function LeftPanel({
  state,
  setState,
  onAnalyze,
  isAnalyzing
}: LeftPanelProps) {
  const updateState = (updates: Partial<MatchState>) => setState(s => ({
    ...s,
    ...updates
  }));
  const toggleForm = (team: 'A' | 'B', index: number) => {
    const currentForm = team === 'A' ? [...state.formA] : [...state.formB];
    const sequence: ('G' | 'E' | 'P' | null)[] = ['G', 'E', 'P', null];
    const currentIndex = sequence.indexOf(currentForm[index]);
    currentForm[index] = sequence[(currentIndex + 1) % sequence.length];
    if (team === 'A') updateState({
      formA: currentForm
    });else updateState({
      formB: currentForm
    });
  };
  const levelOptions = [{
    label: 'Bajo',
    value: 'low',
    colorClass: 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
  }, {
    label: 'Medio',
    value: 'med',
    colorClass: 'bg-amber-500/20 border border-amber-500/30 text-amber-400'
  }, {
    label: 'Alto',
    value: 'high',
    colorClass: 'bg-rose-500/20 border border-rose-500/30 text-rose-400'
  }];
  const motivationOptions = [{
    label: 'Bajo',
    value: 'low',
    colorClass: 'bg-rose-500/20 border border-rose-500/30 text-rose-400'
  }, {
    label: 'Medio',
    value: 'med',
    colorClass: 'bg-amber-500/20 border border-amber-500/30 text-amber-400'
  }, {
    label: 'Alto',
    value: 'high',
    colorClass: 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
  }];
  const suspensionOptions = [{
    label: 'Ninguna',
    value: 'none',
    colorClass: 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
  }, {
    label: 'Menor',
    value: 'minor',
    colorClass: 'bg-amber-500/20 border border-amber-500/30 text-amber-400'
  }, {
    label: 'Crítica',
    value: 'critical',
    colorClass: 'bg-rose-500/20 border border-rose-500/30 text-rose-400'
  }];
  return <div className="flex flex-col gap-6 w-full" data-id="element-22">
      {/* Match Setup */}
      <GlassCard delay={0.1} data-id="element-23">
        <div className="flex items-center gap-2 mb-2" data-id="element-24">
          <Swords className="w-5 h-5 text-emerald-400" data-id="element-25" />
          <h2 className="text-lg font-semibold text-white" data-id="element-26">
            Configuración del Partido
          </h2>
        </div>
        <p className="text-xs text-slate-500 mb-6" data-id="element-27">
          Refleja la probabilidad implícita de la casa de apuestas
        </p>

        <div className="grid grid-cols-[1fr_auto_1fr] gap-4 items-start" data-id="element-28">
          <div className="space-y-4" data-id="element-29">
            <Input label="Equipo A" placeholder="ej. Arsenal" value={state.teamA} onChange={e => updateState({
            teamA: e.target.value
          })} data-id="element-30" />
            <Input label="Cuotas del Mercado (Decimal)" placeholder="2.10" type="number" step="0.01" value={state.oddsA} onChange={e => updateState({
            oddsA: e.target.value
          })} data-id="element-31" />
          </div>

          <div className="flex flex-col items-center justify-center h-full pt-6" data-id="element-32">
            <div className="bg-slate-800/50 rounded-full px-3 py-1 text-xs font-bold text-slate-400 mb-4" data-id="element-33">
              VS
            </div>
            <Toggle checked={state.isHomeA} onChange={c => updateState({
            isHomeA: c
          })} data-id="element-34" />
            <span className="text-[10px] text-slate-500 mt-1 uppercase tracking-wider" data-id="element-35">
              {state.isHomeA ? 'Equipo A Local' : 'Equipo B Local'}
            </span>
          </div>

          <div className="space-y-4" data-id="element-36">
            <Input label="Equipo B" placeholder="ej. Chelsea" value={state.teamB} onChange={e => updateState({
            teamB: e.target.value
          })} data-id="element-37" />
            <Input label="Cuotas del Mercado (Decimal)" placeholder="3.40" type="number" step="0.01" value={state.oddsB} onChange={e => updateState({
            oddsB: e.target.value
          })} data-id="element-38" />
          </div>
        </div>
      </GlassCard>

      {/* Recent Performance */}
      <GlassCard delay={0.15} data-id="element-39">
        <div className="flex items-center gap-2 mb-6" data-id="element-40">
          <TrendingUp className="w-5 h-5 text-blue-400" data-id="element-41" />
          <h2 className="text-lg font-semibold text-white" data-id="element-42">
            Rendimiento Reciente (Últimos 5)
          </h2>
        </div>

        <div className="flex gap-4 mb-5 text-[11px] text-slate-500" data-id="element-43">
          <span data-id="element-44">
            <span className="text-emerald-400 font-bold" data-id="element-45">G</span> = Ganado
          </span>
          <span data-id="element-46">
            <span className="text-amber-400 font-bold" data-id="element-47">E</span> = Empate
          </span>
          <span data-id="element-48">
            <span className="text-rose-400 font-bold" data-id="element-49">P</span> = Perdido
          </span>
        </div>

        <div className="space-y-6" data-id="element-50">
          <div className="flex items-center justify-between" data-id="element-51">
            <span className="text-sm font-medium text-slate-300 w-24 truncate" data-id="element-52">
              {state.teamA || 'Equipo A'}
            </span>
            <div className="flex gap-2" data-id="element-53">
              {state.formA.map((res, i) => <FormPill key={`A-${i}`} result={res} onClick={() => toggleForm('A', i)} data-id="element-54" />)}
            </div>
          </div>
          <div className="flex items-center justify-between" data-id="element-55">
            <span className="text-sm font-medium text-slate-300 w-24 truncate" data-id="element-56">
              {state.teamB || 'Equipo B'}
            </span>
            <div className="flex gap-2" data-id="element-57">
              {state.formB.map((res, i) => <FormPill key={`B-${i}`} result={res} onClick={() => toggleForm('B', i)} data-id="element-58" />)}
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Home vs Away Performance */}
      <GlassCard delay={0.2} data-id="element-59">
        <div className="flex items-center gap-2 mb-6" data-id="element-60">
          <Home className="w-5 h-5 text-indigo-400" data-id="element-61" />
          <h2 className="text-lg font-semibold text-white" data-id="element-62">
            Rendimiento Local vs Visitante
          </h2>
        </div>

        <div className="space-y-5" data-id="element-63">
          <div className="space-y-3" data-id="element-64">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-65">
              {state.teamA || 'Equipo A'}
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-66">
              <Input label="Victoria Local (%)" type="number" value={state.homeWinRateA} onChange={e => updateState({
              homeWinRateA: e.target.value
            })} data-id="element-67" />
              <Input label="Victoria Visitante (%)" type="number" value={state.awayWinRateA} onChange={e => updateState({
              awayWinRateA: e.target.value
            })} data-id="element-68" />
            </div>
          </div>
          <div className="space-y-3" data-id="element-69">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-70">
              {state.teamB || 'Equipo B'}
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-71">
              <Input label="Victoria Local (%)" type="number" value={state.homeWinRateB} onChange={e => updateState({
              homeWinRateB: e.target.value
            })} data-id="element-72" />
              <Input label="Victoria Visitante (%)" type="number" value={state.awayWinRateB} onChange={e => updateState({
              awayWinRateB: e.target.value
            })} data-id="element-73" />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Average Goals */}
      <GlassCard delay={0.25} data-id="element-74">
        <div className="flex items-center gap-2 mb-6" data-id="element-75">
          <Target className="w-5 h-5 text-pink-400" data-id="element-76" />
          <h2 className="text-lg font-semibold text-white" data-id="element-77">
            Promedio de Goles
          </h2>
        </div>

        <div className="space-y-5" data-id="element-78">
          <div className="space-y-3" data-id="element-79">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-80">
              {state.teamA || 'Equipo A'}
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-81">
              <Input label="Anotados (Prom.)" type="number" step="0.1" value={state.avgGoalsScoredA} onChange={e => updateState({
              avgGoalsScoredA: e.target.value
            })} data-id="element-82" />
              <Input label="Recibidos (Prom.)" type="number" step="0.1" value={state.avgGoalsConcededA} onChange={e => updateState({
              avgGoalsConcededA: e.target.value
            })} data-id="element-83" />
            </div>
          </div>
          <div className="space-y-3" data-id="element-84">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-85">
              {state.teamB || 'Equipo B'}
            </div>
            <div className="grid grid-cols-2 gap-3" data-id="element-86">
              <Input label="Anotados (Prom.)" type="number" step="0.1" value={state.avgGoalsScoredB} onChange={e => updateState({
              avgGoalsScoredB: e.target.value
            })} data-id="element-87" />
              <Input label="Recibidos (Prom.)" type="number" step="0.1" value={state.avgGoalsConcededB} onChange={e => updateState({
              avgGoalsConcededB: e.target.value
            })} data-id="element-88" />
            </div>
          </div>
        </div>
      </GlassCard>

      {/* Injury Impact */}
      <GlassCard delay={0.3} data-id="element-89">
        <div className="flex items-center gap-2 mb-6" data-id="element-90">
          <ShieldAlert className="w-5 h-5 text-amber-400" data-id="element-91" />
          <h2 className="text-lg font-semibold text-white" data-id="element-92">
            Nivel de Impacto por Lesiones
          </h2>
        </div>

        <div className="space-y-5" data-id="element-93">
          <div className="space-y-2" data-id="element-94">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-95">
              {state.teamA || 'Equipo A'}
            </div>
            <SegmentedControl id="injury-a" options={levelOptions} value={state.injuryA} onChange={v => updateState({
            injuryA: v
          })} data-id="element-96" />
          </div>
          <div className="space-y-2" data-id="element-97">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-98">
              {state.teamB || 'Equipo B'}
            </div>
            <SegmentedControl id="injury-b" options={levelOptions} value={state.injuryB} onChange={v => updateState({
            injuryB: v
          })} data-id="element-99" />
          </div>
        </div>
      </GlassCard>

      {/* Motivation Level */}
      <GlassCard delay={0.35} data-id="element-100">
        <div className="flex items-center gap-2 mb-6" data-id="element-101">
          <Flame className="w-5 h-5 text-orange-400" data-id="element-102" />
          <h2 className="text-lg font-semibold text-white" data-id="element-103">
            Nivel de Motivación
          </h2>
        </div>

        <div className="space-y-5" data-id="element-104">
          <div className="space-y-2" data-id="element-105">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-106">
              {state.teamA || 'Equipo A'}
            </div>
            <SegmentedControl id="motivation-a" options={motivationOptions} value={state.motivationA} onChange={v => updateState({
            motivationA: v
          })} data-id="element-107" />
          </div>
          <div className="space-y-2" data-id="element-108">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-109">
              {state.teamB || 'Equipo B'}
            </div>
            <SegmentedControl id="motivation-b" options={motivationOptions} value={state.motivationB} onChange={v => updateState({
            motivationB: v
          })} data-id="element-110" />
          </div>
        </div>
      </GlassCard>

      {/* Suspensions */}
      <GlassCard delay={0.4} data-id="element-111">
        <div className="flex items-center gap-2 mb-6" data-id="element-112">
          <UserX className="w-5 h-5 text-rose-400" data-id="element-113" />
          <h2 className="text-lg font-semibold text-white" data-id="element-114">
            Suspensiones / Ausencias Clave
          </h2>
        </div>

        <div className="space-y-5" data-id="element-115">
          <div className="space-y-2" data-id="element-116">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-117">
              {state.teamA || 'Equipo A'}
            </div>
            <SegmentedControl id="suspensions-a" options={suspensionOptions} value={state.suspensionsA} onChange={v => updateState({
            suspensionsA: v
          })} data-id="element-118" />
          </div>
          <div className="space-y-2" data-id="element-119">
            <div className="text-xs text-slate-400 uppercase tracking-wider" data-id="element-120">
              {state.teamB || 'Equipo B'}
            </div>
            <SegmentedControl id="suspensions-b" options={suspensionOptions} value={state.suspensionsB} onChange={v => updateState({
            suspensionsB: v
          })} data-id="element-121" />
          </div>
        </div>
      </GlassCard>

      {/* Action Button */}
      <button onClick={onAnalyze} disabled={isAnalyzing} className="relative w-full group overflow-hidden rounded-xl bg-emerald-500 hover:bg-emerald-400 text-white font-semibold py-4 px-6 transition-all duration-300 shadow-[0_0_20px_rgba(16,185,129,0.3)] hover:shadow-[0_0_30px_rgba(16,185,129,0.5)] disabled:opacity-70 disabled:cursor-not-allowed" data-id="element-122">
        <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]" data-id="element-123" />
        <span className="relative flex items-center justify-center gap-2" data-id="element-124">
          {isAnalyzing ? <>
              <Activity className="w-5 h-5 animate-spin" data-id="element-125" />
              Procesando Inteligencia...
            </> : <>
              <Activity className="w-5 h-5" data-id="element-126" />
              Analizar Apuesta
            </>}
        </span>
      </button>
    </div>;
}